import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buildSelectionTree } from '../utils/folderTreeStructure';

const collectProjectIds = (node) => {
  if (node.type === 'project') {
    return [node.id];
  }
  if (!node.children) {
    return [];
  }
  return node.children.flatMap(collectProjectIds);
};

const TreeNode = React.memo(({ node, depth, selected, onToggle }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const isProject = node.type === 'project';
  const childProjectIds = useMemo(() => collectProjectIds(node), [node]);
  const selectedCount = useMemo(
    () => childProjectIds.filter((id) => selected.has(id)).length,
    [childProjectIds, selected]
  );
  const totalCount = childProjectIds.length;
  const isFullySelected = totalCount > 0 && selectedCount === totalCount;
  const isPartiallySelected = selectedCount > 0 && selectedCount < totalCount;
  const checked = isProject ? selected.has(node.id) : isFullySelected;
  const indeterminate = !isProject && isPartiallySelected;

  const handleToggle = useCallback(() => {
    if (totalCount === 0) {
      return;
    }
    const action = isFullySelected ? 'remove' : 'add';
    onToggle(childProjectIds, action);
  }, [childProjectIds, isFullySelected, onToggle, totalCount]);

  const checkboxRef = useCallback(
    (el) => {
      if (el) {
        el.indeterminate = indeterminate;
      }
    },
    [indeterminate]
  );

  const paddingLeft = depth * 20 + 8;

  return (
    <>
      <div
        className="flex items-center py-1 px-2 rounded group select-none"
        style={{ paddingLeft }}
      >
        {!isProject ? (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="mr-1 w-5 h-5 flex items-center justify-center flex-shrink-0"
            style={{color: 'rgba(255,255,255,0.4)'}}
          >
            <svg
              className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : (
          <span className="mr-1 w-5 h-5 flex-shrink-0" />
        )}

        <input
          ref={checkboxRef}
          type="checkbox"
          checked={checked}
          onChange={handleToggle}
          disabled={totalCount === 0}
          className="mr-2 h-4 w-4 rounded cursor-pointer flex-shrink-0"
          style={{borderColor: 'var(--tqa-border-dark)', accentColor: 'var(--tqa-green)'}}
        />

        {isProject ? (
          <svg className="w-4 h-4 mr-2 flex-shrink-0" style={{color: 'rgba(255,255,255,0.5)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        ) : (
          <svg className="w-4 h-4 mr-2 text-tqa-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
        )}

        <span
          className={`text-sm truncate ${
            isProject ? 'opacity-75' : 'font-medium'
          }`}
          title={node.name}
        >
          {node.name}
        </span>

        {!isProject && totalCount > 0 && (
          <span className="ml-2 text-xs flex-shrink-0" style={{color: 'rgba(255,255,255,0.4)'}}>
            {selectedCount > 0 ? `${selectedCount}/${totalCount}` : totalCount}{' '}
            {totalCount === 1 ? 'project' : 'projects'}
          </span>
        )}
      </div>

      {!isProject && expanded && node.children && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={`${child.type}-${child.id}`}
              node={child}
              depth={depth + 1}
              selected={selected}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </>
  );
});

TreeNode.displayName = 'TreeNode';

const CrossProjectFiltersModal = ({
  isOpen,
  onClose,
  onApply,
  folderTree,
  initialSelectedProjectIds,
  isLoading,
}) => {
  const [selected, setSelected] = useState(new Set(initialSelectedProjectIds || []));

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(initialSelectedProjectIds || []));
    }
  }, [initialSelectedProjectIds, isOpen]);

  const rootNodes = useMemo(() => buildSelectionTree(folderTree), [folderTree]);

  const allProjectIds = useMemo(() => {
    const rootNode = { type: 'root', children: rootNodes };
    return collectProjectIds(rootNode);
  }, [rootNodes]);

  const handleToggle = useCallback((projectIds, action) => {
    setSelected((prev) => {
      const next = new Set(prev);
      projectIds.forEach((id) => {
        if (action === 'add') {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelected(new Set(allProjectIds));
  }, [allProjectIds]);

  const handleDeselectAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  const handleApply = useCallback(() => {
    onApply(Array.from(selected));
  }, [onApply, selected]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="rounded-xl shadow-2xl w-[680px] max-w-[95vw] max-h-[85vh] flex flex-col" style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
        <div className="px-6 py-4 flex-shrink-0" style={{borderBottom: '1px solid var(--tqa-border-dark)'}}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>Select Projects for Analysis</h2>
              <p className="text-sm mt-0.5" style={{color: 'rgba(255,255,255,0.5)'}}>
                Choose folders or individual projects to include in cross-project analysis.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="p-1 rounded-lg transition-colors"
              style={{color: 'rgba(255,255,255,0.4)'}}
              onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-2 flex items-center justify-between flex-shrink-0" style={{borderBottom: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)'}}>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs font-medium hover:underline"
              style={{color: 'var(--tqa-green)'}}
            >
              Select All
            </button>
            <span style={{color: 'rgba(255,255,255,0.2)'}}>|</span>
            <button
              type="button"
              onClick={handleDeselectAll}
              className="text-xs font-medium hover:underline"
              style={{color: 'var(--tqa-green)'}}
            >
              Deselect All
            </button>
          </div>
          <div className="text-xs" style={{color: 'rgba(255,255,255,0.5)'}}>
            {selected.size} of {allProjectIds.length} projects selected
          </div>
        </div>

        <div className="px-4 py-3 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="text-sm text-center py-8" style={{color: 'rgba(255,255,255,0.5)'}}>Loading folders and projects...</div>
          ) : rootNodes.length === 0 ? (
            <div className="text-sm text-center py-8" style={{color: 'rgba(255,255,255,0.5)'}}>No projects available.</div>
          ) : (
            rootNodes.map((node) => (
              <TreeNode
                key={`${node.type}-${node.id}`}
                node={node}
                depth={0}
                selected={selected}
                onToggle={handleToggle}
              />
            ))
          )}
        </div>

        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{borderTop: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)'}}>
          <p className="text-xs" style={{color: 'rgba(255,255,255,0.4)'}}>
            {selected.size === 0 ? 'Select at least one project to continue.' : 'Your selection will be used for cross-project analysis.'}
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary px-4 py-2 text-sm font-medium rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={selected.size === 0}
              className="btn-primary px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossProjectFiltersModal;
